// Thin wrappers around the BiorGov backend endpoints. The original template
// ships with helpers for ~40 demo features (calendar / chat / ecommerce /
// invoices / etc.); those are gone now — only real, actively-called endpoints
// live here.
import { APIClient } from "./api_helper";
import * as url from "./url_helper";

const api = new APIClient();

// ── Auth ────────────────────────────────────────────────────────────────────
export const postJwtLogin     = (data) => api.create(url.POST_JWT_LOGIN, data);
export const postLogout       = () => api.create(url.POST_JWT_LOGOUT, {});
export const getMe            = () => api.get(url.GET_ME);
export const postForgotPassword = (email) => api.create(url.POST_FORGOT_PASSWORD, { email });
export const postResetPassword  = (token, password) =>
  api.create(`${url.POST_RESET_PASSWORD}/${encodeURIComponent(token)}`, { password });
export const postAcceptInvite   = (token, payload) =>
  api.create(`${url.POST_ACCEPT_INVITE}/${encodeURIComponent(token)}`, payload);
export const postChangePassword = (currentPassword, newPassword) =>
  api.create(url.POST_CHANGE_PASSWORD, { currentPassword, newPassword });

// ── User administration ─────────────────────────────────────────────────────
export const getAdminUsers       = () => api.get(url.ADMIN_USERS);
export const getAdminUserById    = (id) => api.get(`${url.ADMIN_USER_BY_ID}/${id}`);
export const postInviteUser      = (payload) => api.create(url.ADMIN_USERS_INVITE, payload);
export const postResendInvite    = (id) => api.create(`${url.ADMIN_USERS_RESEND_INVITE}/${id}/resend-invite`, {});
export const patchAdminUser      = (id, payload) => api.patch(`${url.ADMIN_USER_BY_ID}/${id}`, payload);
export const postDeactivateUser  = (id) => api.create(`${url.ADMIN_USERS_DEACTIVATE}/${id}/deactivate`, {});
export const getAdminRoles       = () => api.get(url.ADMIN_ROLES);
export const getAdminPermissions = () => api.get(url.ADMIN_PERMISSIONS);

// ── Dashboard / Overview ────────────────────────────────────────────────────
export const getDashboardOverview     = (params) => api.get(url.GET_DASHBOARD_OVERVIEW, params);
export const getDashboardMetrics      = () => api.get(url.GET_DASHBOARD_METRICS);
export const getTransactionAnalytics  = (params) => api.get(url.GET_TRANSACTION_ANALYTICS, params);
export const getDashboardSwapStats    = (timeframe) =>
  api.get(url.GET_DASHBOARD_SWAP_STATS, timeframe ? { timeframe } : undefined);
export const exportDashboardData      = (params) => api.get(url.EXPORT_DASHBOARD_DATA, params);
export const syncBybTokenVolume       = () => api.create(url.SYNC_BYB_TOKEN, {});

// ── North Star (Weekly Engaged Wallets) ──────────────────────────────────────
// Backend endpoints not built yet — callers wrap these in `.catch(() => null)`
// and render an "awaiting backend" empty state until they exist.
export const getWewSummary         = () => api.get(url.GET_WEW_SUMMARY);
export const getWewTrend           = (weeks) => api.get(url.GET_WEW_TREND, weeks ? { weeks } : undefined);
export const getWewPassConditions  = () => api.get(url.GET_WEW_PASS);
export const getWewRetention       = (weeks) => api.get(url.GET_WEW_RETENTION, weeks ? { weeks } : undefined);
export const getWewCountermeasures = () => api.get(url.GET_WEW_COUNTERMEASURES);

// ── Chart event annotations ─────────────────────────────────────────────────
export const getChartAnnotations   = (params) => api.get(url.WEW_ANNOTATIONS, params);
export const createChartAnnotation = (payload) => api.create(url.WEW_ANNOTATIONS, payload);
export const deleteChartAnnotation = (id) => api.delete(`${url.WEW_ANNOTATIONS}/${id}`);

// ── Traffic sources ─────────────────────────────────────────────────────────
export const getVisitorSources = (days) => api.get(url.GET_VISITOR_SOURCES, days ? { days } : undefined);

// ── PR discipline KPI ───────────────────────────────────────────────────────
export const getPrDisciplineSummary = () => api.get(url.GET_PR_DISCIPLINE_SUMMARY);
export const getPrDisciplineOpenPrs = () => api.get(url.GET_PR_DISCIPLINE_OPEN_PRS);
export const getPrDisciplineStatus  = () => api.get(url.GET_PR_DISCIPLINE_STATUS);
export const postPrDisciplineSync   = () => api.create(url.POST_PR_DISCIPLINE_SYNC, {});

// ── Founder Daily Tasks (issue #98) ─────────────────────────────────────────
// Founder-only (403 for other roles) — caller hides the card on failure.
export const getDailyTasks     = () => api.get(url.DAILY_TASKS);
export const createDailyTask   = (data) => api.create(url.DAILY_TASKS, data);
export const updateDailyTask   = (id, data) => api.update(`${url.DAILY_TASKS}/${id}`, data);
export const reorderDailyTasks = (order) => api.create(`${url.DAILY_TASKS}/reorder`, { order });
export const archiveDailyTask  = (id) => api.create(`${url.DAILY_TASKS}/${id}/archive`, {});
export const resetDailyTasks   = () => api.create(`${url.DAILY_TASKS}/reset`, {});
export const toggleDailyTask   = (id, done) => api.create(`${url.DAILY_TASKS}/${id}/toggle`, { done });

// ── Notifications ───────────────────────────────────────────────────────────
export const getNotifications    = (params)     => api.get(url.NOTIFICATIONS, params);
export const createNotification  = (data)       => api.create(url.NOTIFICATIONS, data);
export const updateNotification  = (id, data)   => api.patch(`${url.NOTIFICATIONS}/${id}`, data);
export const deleteNotification  = (id)         => api.delete(`${url.NOTIFICATIONS}/${id}`);

// ── Transactions + Cards ────────────────────────────────────────────────────
export const getTransactionHistory    = (params) => api.get(url.GET_TRANSACTION_HISTORY, params);
export const getTransactionStatistics = (params) => api.get(url.GET_TRANSACTION_STATISTICS, params);
export const getTransactionById       = (id) => api.get(`${url.GET_TRANSACTION_BY_ID}/${id}`);
export const updateTransactionStatus  = (id, data) => api.update(`${url.UPDATE_TRANSACTION_STATUS}/${id}/status`, data);
export const getCardsByEmail          = (email) => api.get(`${url.GET_CARDS_BY_EMAIL}/${email}`);
export const getCardById              = (id) => api.get(`${url.GET_CARD_BY_ID}/${id}`);

// ── Paybis ──────────────────────────────────────────────────────────────────
export const getPaybisTransactionList = (params) => api.get(url.GET_PAYBIS_LIST, params);
export const getPaybisCountsByFlow    = () => api.get(url.GET_PAYBIS_COUNTS_BY_FLOW);
export const getPaybisHistory         = (params) => api.get(url.GET_PAYBIS_HISTORY, params);
export const getPaybisStatistics      = (params) => api.get(url.GET_PAYBIS_STATISTICS, params);
export const syncPaybisVolume         = (data) => api.create(url.SYNC_PAYBIS_VOLUME, data);
export const getPaybisCurrencies      = () => api.get(url.GET_PAYBIS_CURRENCIES);
export const testPaybisConnection     = () => api.get(url.TEST_PAYBIS_CONNECTION);

// ── App Metrics ─────────────────────────────────────────────────────────────
export const getAppMetricsSummary    = () => api.get(url.GET_APP_METRICS_SUMMARY);
export const getAppMetricsTotals     = () => api.get(url.GET_APP_METRICS_TOTALS);
export const getAppMetricsGrowth     = (params) => api.get(url.GET_APP_METRICS_GROWTH, params);
export const getAppMetricsCrashes    = (params) => api.get(url.GET_APP_METRICS_CRASHES, params);
export const getAppMetricsByPlatform = (platform, params) =>
  api.get(`${url.GET_APP_METRICS_PLATFORM}/${platform}`, params);
export const runAppMetricsSync       = (data = {}) => api.create(url.POST_APP_METRICS_SYNC, data);

// ── Alerts ──────────────────────────────────────────────────────────────────
export const getAlertsAdmin    = (params) => api.get(url.GET_ALERTS_ADMIN, params);
export const deleteAlertAdmin  = (id) => api.delete(`${url.DELETE_ALERT_ADMIN}/${id}`);

// ── Feedback (admin reads + write paths still wired for parity) ─────────────
export const getFeedbackCategories            = () => api.get(url.GET_FEEDBACK_CATEGORIES);
export const createFeedbackCategory           = (data) => api.create(url.CREATE_FEEDBACK_CATEGORY, data);
export const getFeedbackSuggestions           = (params) => api.get(url.GET_FEEDBACK_SUGGESTIONS, params);
export const getFeedbackSuggestionById        = (id) => api.get(`${url.GET_FEEDBACK_SUGGESTION_BY_ID}/${id}`);
export const updateFeedbackSuggestionStatus   = (id, status) =>
  api.patch(`${url.UPDATE_FEEDBACK_SUGGESTION_STATUS}/${id}/status`, { status });
export const deleteFeedbackSuggestion         = (id) => api.delete(`${url.DELETE_FEEDBACK_SUGGESTION}/${id}`);
export const getFeedbackBugReports            = (params) => api.get(url.GET_FEEDBACK_BUG_REPORTS, params);
export const deleteFeedbackBugReport          = (id) => api.delete(`${url.DELETE_FEEDBACK_BUG_REPORT}/${id}`);
export const getFeedbackDoNotLike             = (params) => api.get(url.GET_FEEDBACK_DO_NOT_LIKE, params);
// Generic feedback status setter (bug reports / do-not-like → e.g. spam).
export const updateFeedbackStatus             = (id, status) => api.patch(`/feedback/${id}/status`, { status });
// Re-file a submission that landed in the wrong place. `feedbackId` is always
// the FEEDBACK id — from the Suggestions tab that means `item.feedback_id`, NOT
// `item.id` (which is a suggestions.id). `title` is optional and only consulted
// when moving into Suggestions on a row that has none.
export const moveFeedbackType                 = (feedbackId, type, title) =>
  api.patch(`/feedback/${feedbackId}/type`, title ? { type, title } : { type });

// ── System checks (super-admin only) ────────────────────────────────────────
export const getSystemChecksDb       = () => api.get(url.GET_SYSTEM_CHECKS_DB);
export const getSystemChecksExternal = (service) =>
  api.get(url.GET_SYSTEM_CHECKS_EXTERNAL, service ? { service } : undefined);

// ── API Status (service_health, RBAC: service_health.view / .manage) ────────
export const getServiceHealthStatus  = () => api.get(url.GET_SERVICE_HEALTH_STATUS);
export const getServiceHealthHistory = (service, hours) =>
  api.get(url.GET_SERVICE_HEALTH_HISTORY, { service, hours });
export const postServiceHealthRunAll = () => api.create(url.POST_SERVICE_HEALTH_RUN, {});
export const postServiceHealthRunOne = (service) =>
  api.create(`${url.POST_SERVICE_HEALTH_RUN_ONE}/${encodeURIComponent(service)}/run`, {});
export const putServiceHealthIgnore = (service, ignored) =>
  api.update(`${url.PUT_SERVICE_HEALTH_IGNORE}/${encodeURIComponent(service)}/ignore`, { ignored });

// ── App store reviews (unified Apple + Google, RBAC: app_metrics.view) ───────
export const getReviews = (params) => api.get(url.GET_REVIEWS, params);

// ── App Errors (PostHog crash / error events) ───────────────────────────────
export const getAppErrors        = (params) => api.get(url.GET_APP_ERRORS, params);
export const getAppErrorsSummary = () => api.get(url.GET_APP_ERRORS_SUMMARY);
export const getAppErrorById     = (id) => api.get(`${url.GET_APP_ERROR_BY_ID}/${id}`);
export const patchAppErrorResolve = (id, resolved) =>
  api.patch(`${url.PATCH_APP_ERROR_RESOLVE}/${id}/resolve`, { resolved });
export const postAppErrorsSync   = () => api.create(url.POST_APP_ERRORS_SYNC, {});

// ── Fees ────────────────────────────────────────────────────────────────────
export const getFeesSummary     = (timeframe) =>
  api.get(url.GET_FEES_SUMMARY, timeframe ? { timeframe } : undefined);
export const getFeesMissing     = (params) => api.get(url.GET_FEES_MISSING, params);
export const getFeeSettlements  = (params) => api.get(url.GET_FEE_SETTLEMENTS, params);
export const postFeesSync       = () => api.create(url.POST_FEES_SYNC, {});
export const getSwapkitStatus   = () => api.get(url.GET_SWAPKIT_STATUS);
export const postSwapkitSync    = () => api.create(url.POST_SWAPKIT_SYNC, {});
export const getRangoStatus     = () => api.get(url.GET_RANGO_STATUS);
export const getRangoSwaps      = (params) => api.get(url.GET_RANGO_SWAPS, params);
export const postRangoSync      = () => api.create(url.POST_RANGO_SYNC, {});

// ── Event Health (ingestion monitoring) ─────────────────────────────────────
export const getEventHealthSummary = (days) =>
  api.get(url.GET_EVENT_HEALTH_SUMMARY, days ? { days } : undefined);
export const getEventHealthErrors  = (params) => api.get(url.GET_EVENT_HEALTH_ERRORS, params);

// ── Token listings (RBAC: token_listings.view / .manage / .sponsor) ─────────
// List params: { status, network_id, category, featured, sponsored, search, page, page_size }
export const getTokenListings      = (params) => api.get(`${url.TOKEN_LISTINGS}/`, params);
export const getTokenListing       = (id) => api.get(`${url.TOKEN_LISTINGS}/${encodeURIComponent(id)}`);
export const lookupTokenListing    = (data) => api.create(url.TOKEN_LISTINGS_LOOKUP, data);
export const createTokenListing    = (data) => api.create(`${url.TOKEN_LISTINGS}/`, data);
export const updateTokenListing    = (id, data) => api.patch(`${url.TOKEN_LISTINGS}/${encodeURIComponent(id)}`, data);
export const delistTokenListing    = (id, reason) =>
  api.create(`${url.TOKEN_LISTINGS}/${encodeURIComponent(id)}/delist`, reason ? { reason } : {});
export const relistTokenListing    = (id) => api.create(`${url.TOKEN_LISTINGS}/${encodeURIComponent(id)}/relist`, {});
export const deleteTokenListing    = (id) => api.delete(`${url.TOKEN_LISTINGS}/${encodeURIComponent(id)}`);
export const approveTokenSponsorship = (id) =>
  api.create(`${url.TOKEN_LISTINGS}/${encodeURIComponent(id)}/sponsorship/approve`, {});
export const revokeTokenSponsorship  = (id) =>
  api.create(`${url.TOKEN_LISTINGS}/${encodeURIComponent(id)}/sponsorship/revoke`, {});
// Multipart upload (field `logo`). The explicit multipart Content-Type stops
// axios from JSON-encoding the FormData (the default POST type is JSON); the
// browser fills in the boundary.
export const uploadTokenLogo = (file) => {
  const fd = new FormData();
  fd.append("logo", file);
  return api.create(url.TOKEN_LISTINGS_LOGO, fd, { headers: { "Content-Type": "multipart/form-data" } });
};

// ── Form Submissions (marketing-site inbox) ─────────────────────────────────
export const getFormSubmissions      = (params) => api.get(url.GET_FORM_SUBMISSIONS, params);
export const getFormSubmissionById   = (id) => api.get(`${url.GET_FORM_SUBMISSION_BY_ID}/${id}`);
export const patchFormSubmission     = (id, data) => api.patch(`${url.PATCH_FORM_SUBMISSION}/${id}`, data);
export const archiveFormSubmission   = (id) => api.patch(`${url.PATCH_FORM_SUBMISSION}/${id}`, { archived: true });
export const unarchiveFormSubmission = (id) => api.patch(`${url.PATCH_FORM_SUBMISSION}/${id}`, { archived: false });
export const deleteFormSubmission    = (id) => api.delete(`${url.DELETE_FORM_SUBMISSION}/${id}`);
export const getFormSubmissionStats  = () => api.get(url.GET_FORM_SUBMISSION_STATS);

// CSV exporter uses fetch() to bypass the axios response interceptor (which
// unwraps `.data` and redirects on 401). In demo mode, returns a synthetic
// blob so the download flow works without a backend.
export const downloadFormSubmissionCsv = async (type, bucket) => {
  try {
    const authUser = JSON.parse(localStorage.getItem("authUser") || "null");
    if (authUser && authUser.demo) {
      return new Blob([
        "id,type,status,submitted_at\n" +
        "demo-fs-1,token,new,2026-05-07\n" +
        "demo-fs-2,integration,reviewing,2026-05-06\n" +
        "demo-fs-3,collab,done,2026-05-03\n",
      ], { type: "text/csv" });
    }
  } catch (_) { /* fall through to real network fetch */ }

  const apiBase = (await import("../config")).default.API_URL;
  const params = new URLSearchParams();
  if (type) params.set("type", type);
  if (bucket !== undefined && bucket !== null && bucket !== "") {
    params.set("bucket", String(bucket));
  }
  const qs = params.toString();
  const path = url.GET_FORM_SUBMISSION_EXPORT + (qs ? `?${qs}` : "");
  // DASH-024: auth now rides the httpOnly cookie (credentials: "include"); the
  // in-memory token is sent as an x-access-token same-tab fallback. We no longer
  // read the JWT out of localStorage (legacy sessions fall back to it once).
  const { getSessionToken } = await import("./jwt-token-access/sessionToken");
  let token = getSessionToken();
  if (!token) {
    const authUser = JSON.parse(localStorage.getItem("authUser") || "null");
    token = authUser && authUser.accessToken && authUser.accessToken.startsWith("Bearer ")
      ? authUser.accessToken.slice(7)
      : authUser && authUser.accessToken;
  }

  const response = await fetch(`${apiBase}${path}`, {
    credentials: "include",
    headers: token ? { "x-access-token": token } : {},
  });
  if (!response.ok) {
    if (response.status === 401) {
      localStorage.removeItem("authUser");
      window.location.href = "/login";
    }
    throw new Error(`Export failed (${response.status})`);
  }
  return response.blob();
};

// ── Rewards (Wank Rewards engine) ────────────────────────────────────────
export const getRewardsPayouts    = () => api.get(url.GET_REWARDS_PAYOUTS);
export const recordRewardsPayout  = (id, txHash) => api.create(`${url.POST_REWARDS_PAYOUT}/${id}/record`, { tx_hash: txHash });
export const cancelRewardsPayout  = (id) => api.create(`${url.POST_REWARDS_PAYOUT}/${id}/cancel`, {});
export const getRewardsVault      = () => api.get(url.GET_REWARDS_VAULT);
export const refillRewardsVault   = (amountByb) => api.create(url.POST_REWARDS_VAULT_REFILL, { amount_byb: amountByb });
export const createRewardsDropCode = (data) => api.create(url.POST_REWARDS_DROP_CODE, data);
export const publishRewardsPrice  = (data) => api.create(url.POST_REWARDS_PRICE_PUBLISH, data);
export const getRewardsPrice      = () => api.get(url.GET_REWARDS_PRICE);
export const replayRewardsCredit  = (pollId) => api.create(`${url.POST_REWARDS_REPLAY_CREDIT}/${encodeURIComponent(pollId)}`, {});
// Explorer (read-only): { search, page, page_size }
export const getRewardsWallets    = (params) => api.get(url.GET_REWARDS_WALLETS, params);
export const getRewardsWallet     = (address) => api.get(`${url.GET_REWARDS_WALLETS}/${encodeURIComponent(address)}`);
export const getRewardsReferrals  = (params) => api.get(url.GET_REWARDS_REFERRALS, params);
export const getRewardsTopReferrers = (params) => api.get(url.GET_REWARDS_TOP_REFERRERS, params); // { limit }
export const getRewardsReferralStats = () => api.get(url.GET_REWARDS_REFERRAL_STATS);
// v2: claims queue ({ status, page, page_size }) + drops
export const getRewardsClaims     = (params) => api.get(url.GET_REWARDS_CLAIMS, params);
export const createRewardsDropCodesBulk = (data) => api.create(url.POST_REWARDS_DROP_CODES_BULK, data);
export const getRewardsDropCodes  = (params) => api.get(url.GET_REWARDS_DROP_CODES, params); // { page, page_size, redeemed }
export const createRewardsWhitelistDrop = (data) => api.create(url.POST_REWARDS_DROP_WHITELIST, data);
export const createRewardsSnapshotDrop  = (data) => api.create(url.POST_REWARDS_DROP_SNAPSHOT, data);
export const getRewardsDrops      = () => api.get(url.GET_REWARDS_DROPS);
